import type { CopyShape } from "@tami/core/intl";

import type { HOME_COPY_PT_BR } from "../home.copy";

export const HOME_COPY_EN_US: CopyShape<typeof HOME_COPY_PT_BR> = {
  taglines: {
    connect: {
      before: "Connect every piece of your Creator life in",
      italic: "one place.",
    },
    transformLine1: {
      before: "Turn chaos into",
      italic: "productivity.",
    },
    transformLine2: {
      before: "And money stress into",
      italic: "total control.",
    },
    closing: {
      before: "You create.",
      italic: "Tãmi handles the rest.",
    },
    testimonials: {
      before: "Don't take our word for it.",
      italic: "Take theirs.",
    },
  },

  hero: {
    stickersAlt: "Decorative stickers",
    headingLead: "Your Creator career,",
    headingHighlights: ["Organized.", "Professional.", "Profitable."],
    description:
      "Tãmi is the tool that helps you become the Creator you've always wanted to be: efficient, profitable and with time for what really matters.",
    photoAlt: "young women wearing 2000s fashion",
    scrollDownAria: "Scroll down",
    events: [
      { title: "On-location shoot - Brand deal X", timeRange: "16:30 - 17:30" },
      { title: "Film organic content", timeRange: "19:00 - 22:00" },
      { title: "Speech therapist appointment", timeRange: "10:00 - 12:00" },
      { title: "Alignment call", timeRange: "08:30 - 09:30" },
    ],
  },

  navigationButtons: {
    content: "Content",
    finances: "Finances",
    agenda: "Calendar",
    team: "Team",
    createAccount: "Create your account",
    iconAlt: "Tami icon",
  },

  features: {
    heading: {
      before: "Not sure",
      where: "where",
      start: "to start",
      afterStart: "?",
      followSteps: "follow the steps of",
      brand: "Tãmi",
      pro: "Pro",
    },
    cta: "I want my access now",
    steps: [
      {
        title: "Creative storm",
        description:
          "Where it all begins: an idea, a spark, an insight out of nowhere. The secret? Write it down before it's gone.",
      },
      {
        title: "Gut check",
        description:
          "Time to think it over: does this idea actually make sense? Does it fit me, where I'm at, the people who follow me?",
      },
      {
        title: "Strategic planning",
        description: "The idea made the cut? Now it needs a format, data, a goal and an action plan.",
      },
      {
        title: "Making it happen",
        description: "Hands on! Time to create, film, review or keep tabs on whoever's executing.",
      },
      {
        title: "Creative replay",
        description:
          "The content is out in the world. Now what? Analyze it, figure out what worked and use it as fuel for the next idea.",
      },
    ],
  },

  featuresSession: {
    cards: [
      { title: "Finances", subtitle: "for creators" },
      { title: "Calculator", subtitle: "for brand deals" },
      { title: "Calendar", subtitle: "for creators" },
    ],
    imageAlt: "Group of phones showing Tãmi features",
  },

  bannerImage: {
    imageAlt: "Person working on an iPad showing the creator calendar in Tãmi",
    glassCard: {
      titleLine1: "Break free from spreadsheets",
      titleLine2: "and scattered notes.",
      paragraphLine1: "Bring your creator life together and",
      paragraphLine2: "focus on what really matters.",
      paragraphBold: "Creating.",
      cta: "Create your account",
      iconAlt: "Tami icon",
    },
  },

  bannerPhone: {
    title: "Never miss an idea or a deadline again.",
    imageAlt: "Tami app on an iPhone with a gradient background",
    text: "With Tãmi, you've got a smart assistant that goes wherever you go.",
    cta: "Create your account",
  },

  bannerImgBody: {
    imageAlt: "Promotion Footer",
    logoAlt: "Tãmi logo",
    taglineLine1: "A complete solution, made in Brazil,",
    taglineLine2: "to help Creators grow.",
  },

  heroScrollDemo: {
    rainbowCardAlt: "Rainbow card",
    tags: {
      content: "Content",
      finances: "Finances",
      agenda: "Calendar",
      team: "Team",
    },
    createAccount: "Create your account",
    iconAlt: "Tami icon",
    desktopSlides: [
      "Dashboard - Main panel",
      "Dashboard - Detailed view",
      "Creator performance - Page 1",
      "Creator performance - Page 2",
      "Creator performance - Page 3",
      "Brand deal calculator - Page 1",
      "Brand deal calculator - Page 2",
      "Creator finances - Page 1",
      "Creator finances - Page 2",
      "Creator finances - Page 3",
      "Campaign hub",
      "Tasks and reminders",
      "Creator notes",
      "Calendar",
      "Weekly flow",
    ],
    mobileSlides: [
      "Dashboard - Main panel",
      "Dashboard - Detailed view",
      "Dashboard - Advanced analysis",
      "Dashboard - Reports",
      "Creator performance - Page 1",
      "Creator performance - Page 2",
      "Creator performance - Page 3",
      "Creator performance - Page 4",
      "Brand deal calculator - Page 1",
      "Brand deal calculator - Page 2",
      "Brand deal calculator - Page 3",
      "Creator finances - Page 1",
      "Creator finances - Page 2",
      "Creator finances - Page 3",
      "Creator finances - Page 4",
      "Campaign hub",
      "Tasks and reminders",
      "Creator notes",
      "Calendar",
      "Weekly flow",
    ],
  },

  infoMessage: {
    titleBold1: "Tired",
    titleDe: "of",
    titleTools: "tools",
    titleThatDont: "that don't",
    titleBold2: "get you",
    titleQuestion: "?",
    bodyBefore: "While other platforms tried to",
    bodyItalic: "squeeze creators",
    bodyAfter:
      "into a rigid corporate mold, Tãmi was born from our own real mess: the packed routine, the tight deadlines and the overloaded brain of people who create for a living.",
    tagline: "Here to tame the chaos of the creator hustle.",
    cta: "I want to get organized with Tãmi",
  },

  blackFriday: {
    label: "TÃMI BLACK FRIDAY IS ON!",
    iconAlt: "Tãmi",
  },

  testimonials: {
    sectionAria: "Creator testimonials",
    memberSince: "on Tãmi since",
  },

  pricing: {
    title: {
      line1: "Our prices, straight up,",
      line2: "no hidden fees.",
    },
    subtitle: "Tãmi is for creators at every stage of their career.",
    audienceAria: "Choose the account type",
    audienceTabs: {
      creator: "I'm a Creator",
      agency: "I'm a Collab",
    },
    cycleAnnualAria: "Annual billing",
    monthly: "Monthly",
    annual: "Annual",
    bridge: {
      toCreator: "Creating content on your own? The individual plans are under “I'm a Creator”.",
      toCollab: {
        before: "Managing creators? Check out",
        bold: "Tãmi Enterprise",
        after: "under “I'm a Collab”.",
      },
    },
  },

  planCard: {
    mostChosen: "Most popular",
    onRequestDescription: "Our team puts together the right proposal with you.",
    free: "Free",
    freeForever: "Forever, with the limits below.",
    perYear: "/year",
    perMonth: "/mo",
    includedFree: "What's included",
    includedPaid: "What you get",
  },

  benefits: {
    title: "Is Tãmi right for you?",
    items: {
      balance: {
        title: "Better Work-Life Balance",
        description: "Makes time management easier, bringing more balance between your personal and professional life.",
      },
      empowerment: {
        title: "Professional Empowerment",
        description: "Gives you tools for more strategic management and career growth.",
      },
      stress: {
        title: "Less Stress",
        description: "Cuts down the admin overload and the stress of juggling multiple projects.",
      },
      clarity: {
        title: "More Clarity and Focus",
        description: "Helps you set and reach clear goals, giving you focus and direction.",
      },
      relationships: {
        title: "Stronger Business Relationships",
        description: "Improves how you manage partnerships and negotiations, strengthening business relationships.",
      },
      growth: {
        title: "Personal and Professional Growth",
        description: "Supports continuous development by streamlining how you manage your work.",
      },
    },
  },

  planComparison: {
    title: "Compare line by line",
    subtitle: "The numbers come from the limits set on each plan.",
    caption: "Comparison of features and limits across Tãmi plans",
    featureColumn: "Feature",
  },

  priceOnRequest: {
    title: "Talk to us",
  },

  featureList: {
    seeLess: "See less",
    seeMore: (count: number): string => `See ${count} more ${count === 1 ? "item" : "items"}`,
  },

  enterpriseCard: {
    seatsPreview: "you + 1 creator, and it grows when you need it",
    onRequestDescription: "Collab package plus a price per creator. Our team puts together the right proposal with you.",
    packageLabel: "Collab package",
    perYear: "/year",
    perMonth: "/mo",
    perCreator: (formattedValue: string): string => `+ ${formattedValue} per creator`,
    badge: "For Collabs",
    logoAlt: "Tami Logo",
    intro:
      "For Collabs that manage creators and their campaigns. One single dashboard for the whole Collab — and every creator in the Collab gets the full Tãmi Pro, no separate subscription.",
    ctaTalk: "Talk about Collab",
    bottomCta: {
      before: "I want to talk about",
      bold: "Tãmi for Collabs",
    },
    bottomCtaSignUp: {
      before: "I want Tãmi for my",
      bold: "Collab!",
    },
    features: [
      {
        title: "Collab creators",
        description: "Add and organize all of the Collab's creators in one dashboard, with a combined view.",
      },
      {
        title: "Creator linking",
        description: "Invite people who already use Tãmi or create new accounts for the Collab, with controlled access.",
      },
      {
        title: "Collab campaigns",
        description: "Track each creator's campaigns and delivery status in one place.",
      },
      {
        title: "Collab proposals",
        description: "Build sales proposals that add up the reach of multiple creators, and export them as PDF.",
      },
      {
        title: "Per-seat subscription",
        description: "A single subscription for the Collab, with seats that grow as the Collab grows.",
      },
      {
        title: "Everything in Tãmi Pro",
        description: "Every creator in the Collab uses the full Pro plan tools, no separate subscription.",
      },
    ],
  },
};
